/**
 * desktop-session-keepalive — the session must survive the app being ignored.
 *
 * THE BUG (08-04, founder-hit). Supabase's browser default STOPS token
 * auto-refresh the moment `document.hidden` goes true, and restarts it only
 * when the page is visible again. That is right for a tab you switched away
 * from and wrong for a desktop app, whose window sits behind Chrome for hours
 * and must still be signed in when its owner looks back at it.
 *
 * What actually happened: the last session push was 03:57, the window went
 * behind other apps, no refresh ever fired again, the access token expired at
 * 04:48, and eight hours later the composer sent a message onto a socket the
 * server refused (`invalid JWT … token is expired`). The UI showed "Thinking"
 * forever, because nothing on the failing path says what went wrong. The
 * founder's verdict, and it is the requirement: "who would relaunch app after
 * a while of inactivity".
 *
 * So, in the desktop shell only:
 *  - re-assert auto-refresh every time the page goes hidden, because that is
 *    exactly when Supabase turns it off;
 *  - on the way back to visible, force a session read, which refreshes an
 *    already-expired token. Timers can be frozen outright (machine asleep), so
 *    a beat alone is not enough — the return has to converge too.
 *
 * Level-triggered, not push: both halves re-check the world rather than trust
 * that some earlier event fired. Inert in a real browser, where the visibility
 * behavior is correct and the tab's own refresh is the user's expectation.
 */

/** The slice of the Supabase auth client this needs — keeps the seam testable. */
export interface KeepaliveAuth {
  startAutoRefresh: () => Promise<unknown> | unknown;
  getSession: () => Promise<unknown>;
}

export interface KeepaliveHost {
  addEventListener: (type: string, listener: () => void) => void;
  removeEventListener: (type: string, listener: () => void) => void;
  /** Reads `document.hidden` at call time — never a cached snapshot. */
  isHidden: () => boolean;
}

/**
 * What to do for a given visibility state. Pure — tested.
 *
 * Hidden: Supabase has just stopped the refresh loop, so start it again.
 * Visible: the loop is running again on its own, but the token may already have
 * died while we were away, so read the session to force a catch-up refresh.
 */
export type KeepaliveAction = "reassert-refresh" | "catch-up";

export function actionForVisibility(hidden: boolean): KeepaliveAction {
  return hidden ? "reassert-refresh" : "catch-up";
}

/**
 * Keep `auth`'s token refreshing regardless of window visibility.
 * Returns a teardown. Call ONLY in the desktop shell.
 */
export function keepDesktopSessionAlive(
  auth: KeepaliveAuth,
  host: KeepaliveHost
): () => void {
  const apply = (action: KeepaliveAction): void => {
    if (action === "reassert-refresh") {
      // Fire-and-forget: a failed start must never throw into a visibility handler.
      void Promise.resolve(auth.startAutoRefresh()).catch(() => {});
    } else {
      // getSession() refreshes an expired token as a side effect, which emits
      // TOKEN_REFRESHED — AuthContext's existing listener re-pushes the fresh
      // session to the native shell from there, so nothing is duplicated here.
      void auth.getSession().catch(() => {});
    }
  };

  // Start refreshing NOW rather than waiting for the first visibility change:
  // the app may well launch into the background (login item, reopened window).
  void Promise.resolve(auth.startAutoRefresh()).catch(() => {});

  const onVisibility = (): void => apply(actionForVisibility(host.isHidden()));
  host.addEventListener("visibilitychange", onVisibility);
  return () => host.removeEventListener("visibilitychange", onVisibility);
}
